import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useFindOne, useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Grid,
  GridItem,
  List,
  ListItem,
  ListIcon,
  UnorderedList,
} from "@chakra-ui/react";
import { ArrowBackIcon, InfoIcon, CheckCircleIcon } from "@chakra-ui/icons";

export default function ImmersiveScenarioGenerator() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scenarioId = searchParams.get("scenarioId");
  const user = useUser();
  
  // Simulation state
  const [simulationActive, setSimulationActive] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentState, setCurrentState] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [textInput, setTextInput] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [eventLog, setEventLog] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Global action hooks
  const [{ data: initData, fetching: initFetching, error: initError }, initializeSimulation] = useGlobalAction(api.initializeSimulationState);
  const [{ data: canonicalData, fetching: canonicalFetching, error: canonicalError }, canonicalizeAction] = useGlobalAction(api.canonicalizeAction);
  const [{ data: simulateData, fetching: simulateFetching, error: simulateError }, simulateActions] = useGlobalAction(api.simulateActions);

  // Fetch scenario if scenarioId is provided
  const [{ data: scenario, fetching, error }] = useFindOne(
    api.clinicalScenario,
    scenarioId || "",
    {
      skip: !scenarioId,
      select: {
        id: true,
        title: true,
        description: { markdown: true, truncatedHTML: true },
        scenarioOutput: true,
        ageRange: true,
        levelOfCare: true,
        majorSystemInvolved: true,
        essentialSkills: true,
      },
    }
  );

  const handleGenerateVideo = async () => {
    setVideoGenerating(true);
    // Placeholder for video generation - will be implemented in Phase 2
    setTimeout(() => {
      setVideoGenerating(false);
      setVideoGenerated(true);
    }, 2000);
  };

  const parseScenarioOutput = (output) => {
    if (!output) return null;
    try {
      return typeof output === "string" ? JSON.parse(output) : output;
    } catch (e) {
      return null;
    }
  };

  const scenarioData = parseScenarioOutput(scenario?.scenarioOutput);

  // Timer effect
  useEffect(() => {
    if (simulationActive && !isPaused && startTime) {
      const interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [simulationActive, isPaused, startTime]);

  // Handle simulation initialization
  const handleStartSimulation = async () => {
    if (!scenario || !scenarioData) return;
    
    try {
      const result = await initializeSimulation({
        scenarioId: scenario.id,
        userId: user?.id
      });
      
      if (result) {
        setSessionId(result.sessionId);
        setCurrentState(result.state);
        setSimulationActive(true);
        setStartTime(Date.now());
        setConversationHistory([{
          role: "system",
          content: result.state.narrative || "Simulation started. What are your initial actions?",
          timestamp: new Date().toISOString()
        }]);
        setEventLog([{
          time: "00:00",
          event: "Simulation started"
        }]);
      }
    } catch (error) {
      console.error("Failed to start simulation:", error);
    }
  };

  // Handle voice recording
  const handleStartRecording = () => {
    setIsRecording(true);
    // Web Speech API would go here - for now just a placeholder
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    // Process recorded audio here
    // For Phase 2a, we'll use manual text input instead
  };

  // Handle manual text input submission
  const handleSubmitAction = async () => {
    if (!textInput.trim() || !sessionId || !currentState) return;
    
    const userInput = textInput.trim();
    setTextInput("");
    
    // Add user message to conversation
    const userMessage = {
      role: "user",
      content: userInput,
      timestamp: new Date().toISOString()
    };
    setConversationHistory(prev => [...prev, userMessage]);
    
    try {
      // Canonicalize the action
      const canonicalResult = await canonicalizeAction({
        userInput: userInput,
        currentState: currentState
      });
      
      if (canonicalResult && canonicalResult.actionSet) {
        // Add parsed actions to event log
        const timeStr = formatElapsedTime(elapsedTime);
        canonicalResult.actionSet.forEach(action => {
          setEventLog(prev => [...prev, {
            time: timeStr,
            event: `${action.type}: ${action.details || ''}`
          }]);
        });
        
        // Simulate the actions
        const simulateResult = await simulateActions({
          scenarioData: scenarioData,
          currentState: currentState,
          actionSet: canonicalResult.actionSet
        });
        
        if (simulateResult) {
          // Update state
          setCurrentState(simulateResult.newState);
          setTotalScore(simulateResult.totalScore || totalScore);
          if (simulateResult.scoreBreakdown) {
            setScoreBreakdown(simulateResult.scoreBreakdown);
          }
          
          // Add system response to conversation
          setConversationHistory(prev => [...prev, {
            role: "system",
            content: simulateResult.newState.narrative || "Action processed.",
            timestamp: new Date().toISOString()
          }]);
          
          // Add critical errors to conversation if any
          if (simulateResult.newState.critical_errors && simulateResult.newState.critical_errors.length > 0) {
            simulateResult.newState.critical_errors.forEach(error => {
              setConversationHistory(prev => [...prev, {
                role: "error",
                content: error,
                timestamp: new Date().toISOString()
              }]);
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to process action:", error);
      setConversationHistory(prev => [...prev, {
        role: "error",
        content: "Failed to process action. Please try again.",
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Handle pause/resume
  const handleTogglePause = () => {
    setIsPaused(!isPaused);
  };

  // Handle end simulation
  const handleEndSimulation = () => {
    setSimulationActive(false);
    setIsPaused(false);
  };

  // Format elapsed time
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Get vital sign color
  const getVitalColor = (vital, value) => {
    if (!value) return "gray";
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    switch(vital) {
      case 'HR':
        if (numValue < 50 || numValue > 120) return "red";
        if (numValue < 60 || numValue > 100) return "yellow";
        return "green";
      case 'BP_systolic':
        if (numValue < 90 || numValue > 180) return "red";
        if (numValue < 100 || numValue > 140) return "yellow";
        return "green";
      case 'RR':
        if (numValue < 8 || numValue > 30) return "red";
        if (numValue < 12 || numValue > 20) return "yellow";
        return "green";
      case 'SpO2':
        if (numValue < 90) return "red";
        if (numValue < 95) return "yellow";
        return "green";
      case 'GCS':
        if (numValue < 9) return "red";
        if (numValue < 13) return "yellow";
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <Box bg="gray.900" minH="100vh" color="white">
      <Container maxW="container.xl" py={8}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb mb={6} color="gray.400">
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/scenario-generator">
              Scenario Generator
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Immersive Generator</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Back Button */}
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          colorScheme="blue"
          mb={6}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        {/* Header */}
        <VStack align="stretch" spacing={4} mb={8}>
          <Heading size="2xl" color="blue.400">
            Immersive Scenario Generator
          </Heading>
          <Text fontSize="lg" color="gray.300">
            Creates video-based interactive simulations from clinical scenarios
          </Text>
          {scenario && (
            <Text fontSize="md" color="blue.300" fontWeight="semibold">
              Loaded Scenario: {scenario.title}
            </Text>
          )}
        </VStack>

        {/* Error Handling */}
        {!scenarioId && (
          <Alert status="warning" bg="orange.900" borderRadius="md" mb={6}>
            <AlertIcon />
            <Box>
              <AlertTitle>No Scenario Selected</AlertTitle>
              <AlertDescription>
                Please select a scenario from the{" "}
                <Link
                  to="/scenario-generator"
                  style={{ textDecoration: "underline", color: "#63b3ed" }}
                >
                  scenario generator
                </Link>{" "}
                or{" "}
                <Link
                  to="/my-scenarios"
                  style={{ textDecoration: "underline", color: "#63b3ed" }}
                >
                  my scenarios
                </Link>
                .
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert status="error" bg="red.900" borderRadius="md" mb={6}>
            <AlertIcon />
            <Box>
              <AlertTitle>Error Loading Scenario</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Box>
          </Alert>
        )}

        {fetching && (
          <Box textAlign="center" py={12}>
            <Spinner size="xl" color="blue.400" thickness="4px" />
            <Text mt={4} color="gray.400">
              Loading scenario...
            </Text>
          </Box>
        )}

        {/* Main Content - Only show if scenario is loaded */}
        {scenario && scenarioData && (
          <VStack align="stretch" spacing={6}>
            {/* Scenario Overview Card */}
            <Card bg="gray.800" borderColor="gray.700">
              <CardHeader>
                <Heading size="lg" color="blue.400">
                  Scenario Overview
                </Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" color="gray.400" mb={2}>
                      Title
                    </Text>
                    <Text fontSize="xl">{scenario.title}</Text>
                  </Box>

                  {scenario.description?.markdown && (
                    <Box>
                      <Text fontWeight="bold" color="gray.400" mb={2}>
                        Description
                      </Text>
                      <Text>{scenario.description.markdown}</Text>
                    </Box>
                  )}

                  <Divider borderColor="gray.700" />

                  {/* Patient Demographics */}
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    {scenarioData.patient?.age && (
                      <GridItem>
                        <Text fontWeight="bold" color="gray.400" mb={1}>
                          Age
                        </Text>
                        <Text>{scenarioData.patient.age}</Text>
                      </GridItem>
                    )}
                    {scenarioData.patient?.sex && (
                      <GridItem>
                        <Text fontWeight="bold" color="gray.400" mb={1}>
                          Sex
                        </Text>
                        <Text>{scenarioData.patient.sex}</Text>
                      </GridItem>
                    )}
                    {scenarioData.chiefComplaint && (
                      <GridItem colSpan={2}>
                        <Text fontWeight="bold" color="gray.400" mb={1}>
                          Chief Complaint
                        </Text>
                        <Text>{scenarioData.chiefComplaint}</Text>
                      </GridItem>
                    )}
                  </Grid>

                  {/* Initial Vitals */}
                  {scenarioData.initialVitals && (
                    <>
                      <Divider borderColor="gray.700" />
                      <Box>
                        <Text fontWeight="bold" color="gray.400" mb={3}>
                          Initial Vitals
                        </Text>
                        <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                          {Object.entries(scenarioData.initialVitals).map(
                            ([key, value]) => (
                              <GridItem key={key}>
                                <Card bg="gray.900" size="sm">
                                  <CardBody>
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      textTransform="uppercase"
                                    >
                                      {key.replace(/([A-Z])/g, " $1").trim()}
                                    </Text>
                                    <Text fontSize="lg" fontWeight="bold">
                                      {value}
                                    </Text>
                                  </CardBody>
                                </Card>
                              </GridItem>
                            )
                          )}
                        </Grid>
                      </Box>
                    </>
                  )}

                  {/* Environment */}
                  {scenarioData.environment && (
                    <>
                      <Divider borderColor="gray.700" />
                      <Box>
                        <Text fontWeight="bold" color="gray.400" mb={2}>
                          Environment
                        </Text>
                        <Text>{scenarioData.environment}</Text>
                      </Box>
                    </>
                  )}

                  {/* Observable Findings */}
                  {scenarioData.observableFindings &&
                    scenarioData.observableFindings.length > 0 && (
                      <>
                        <Divider borderColor="gray.700" />
                        <Box>
                          <Text fontWeight="bold" color="gray.400" mb={2}>
                            Observable Findings
                          </Text>
                          <HStack flexWrap="wrap" spacing={2}>
                            {scenarioData.observableFindings.map(
                              (finding, idx) => (
                                <Badge
                                  key={idx}
                                  colorScheme="blue"
                                  fontSize="sm"
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                >
                                  {finding}
                                </Badge>
                              )
                            )}
                          </HStack>
                        </Box>
                      </>
                    )}
                </VStack>
              </CardBody>
            </Card>

            {/* Simulation Control Section */}
            {!simulationActive ? (
              <Card bg="gray.800" borderColor="gray.700">
                <CardHeader>
                  <Heading size="lg" color="blue.400">
                    Start Simulation
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Text color="gray.300">
                      Ready to begin interactive voice-based simulation
                    </Text>

                    <Button
                      colorScheme="blue"
                      size="lg"
                      onClick={handleStartSimulation}
                      isLoading={initFetching}
                      loadingText="Initializing..."
                    >
                      Start Simulation
                    </Button>

                    {initError && (
                      <Alert status="error" bg="red.900" borderRadius="md">
                        <AlertIcon />
                        <Text>Failed to start simulation: {initError.message}</Text>
                      </Alert>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <>
                {/* Active Simulation Interface */}
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  {/* Vitals Monitor */}
                  <GridItem>
                    <Card bg="gray.800" borderColor="gray.700">
                      <CardHeader>
                        <Heading size="md" color="green.400">
                          Vital Signs
                        </Heading>
                      </CardHeader>
                      <CardBody>
                        <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                          {currentState?.vitals && Object.entries(currentState.vitals).map(([key, value]) => {
                            const color = getVitalColor(key, value);
                            return (
                              <GridItem key={key}>
                                <Card bg="gray.900" size="sm" borderColor={`${color}.500`} borderWidth="2px">
                                  <CardBody>
                                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                                      {key.replace(/_/g, ' ')}
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold" color={`${color}.400`}>
                                      {value}
                                    </Text>
                                  </CardBody>
                                </Card>
                              </GridItem>
                            );
                          })}
                        </Grid>
                      </CardBody>
                    </Card>
                  </GridItem>

                  {/* Score Display */}
                  <GridItem>
                    <Card bg="gray.800" borderColor="gray.700">
                      <CardHeader>
                        <Heading size="md" color="blue.400">
                          Performance
                        </Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4}>
                          <Box textAlign="center">
                            <Text fontSize="4xl" fontWeight="bold" color="blue.400">
                              {totalScore}
                            </Text>
                            <Text color="gray.400">Total Score</Text>
                          </Box>
                          <Badge
                            colorScheme={totalScore >= 80 ? "green" : totalScore >= 60 ? "yellow" : "red"}
                            fontSize="lg"
                            px={4}
                            py={2}
                            borderRadius="full"
                          >
                            {totalScore >= 80 ? "Excellent" : totalScore >= 60 ? "Good" : "Needs Improvement"}
                          </Badge>
                          <Text fontSize="sm" color="gray.500" textAlign="center">
                            Time: {formatElapsedTime(elapsedTime)}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>
                </Grid>

                {/* Conversation Display */}
                <Card bg="gray.800" borderColor="gray.700">
                  <CardHeader>
                    <Heading size="md" color="blue.400">
                      Conversation
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack
                      align="stretch"
                      spacing={3}
                      maxH="400px"
                      overflowY="auto"
                      p={2}
                    >
                      {conversationHistory.map((msg, idx) => (
                        <Box
                          key={idx}
                          alignSelf={msg.role === "user" ? "flex-end" : "flex-start"}
                          maxW="70%"
                        >
                          <Card
                            bg={msg.role === "user" ? "blue.900" : msg.role === "error" ? "red.900" : "gray.700"}
                            size="sm"
                          >
                            <CardBody>
                              <Text fontSize="sm">{msg.content}</Text>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </Text>
                            </CardBody>
                          </Card>
                        </Box>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Action Input */}
                <Card bg="gray.800" borderColor="gray.700">
                  <CardHeader>
                    <Heading size="md" color="blue.400">
                      Your Actions
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4}>
                      <HStack width="100%" spacing={4}>
                        <Box flex="1">
                          <input
                            type="text"
                            placeholder="Type your actions here (e.g., 'Give aspirin 324mg PO, start IV, apply oxygen')"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmitAction();
                              }
                            }}
                            disabled={isPaused || canonicalFetching || simulateFetching}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '6px',
                              border: '1px solid #4A5568',
                              backgroundColor: '#2D3748',
                              color: 'white',
                              fontSize: '16px'
                            }}
                          />
                        </Box>
                        <Button
                          colorScheme="blue"
                          onClick={handleSubmitAction}
                          isLoading={canonicalFetching || simulateFetching}
                          isDisabled={!textInput.trim() || isPaused}
                        >
                          Send
                        </Button>
                      </HStack>

                      {(canonicalError || simulateError) && (
                        <Alert status="error" bg="red.900" borderRadius="md">
                          <AlertIcon />
                          <Text>
                            {canonicalError?.message || simulateError?.message}
                          </Text>
                        </Alert>
                      )}

                      <Box width="100%">
                        <Text fontSize="sm" color="gray.500" mb={2}>
                          Push-to-talk voice input coming in Phase 2b
                        </Text>
                        <Button
                          colorScheme="red"
                          size="lg"
                          width="100%"
                          isDisabled={true}
                          leftIcon={isRecording ? <CheckCircleIcon /> : undefined}
                        >
                          {isRecording ? "Recording..." : "Hold to Record (Coming Soon)"}
                        </Button>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Event Timeline */}
                <Card bg="gray.800" borderColor="gray.700">
                  <CardHeader>
                    <Heading size="md" color="blue.400">
                      Event Timeline
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack
                      align="stretch"
                      spacing={2}
                      maxH="300px"
                      overflowY="auto"
                    >
                      {eventLog.map((event, idx) => (
                        <HStack key={idx} spacing={3}>
                          <Badge colorScheme="blue" minW="50px">
                            {event.time}
                          </Badge>
                          <Text fontSize="sm" color="gray.300">
                            {event.event}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Session Controls */}
                <Card bg="gray.800" borderColor="gray.700">
                  <CardBody>
                    <HStack spacing={4} justify="center">
                      <Button
                        colorScheme={isPaused ? "green" : "yellow"}
                        onClick={handleTogglePause}
                      >
                        {isPaused ? "Resume" : "Pause"}
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={handleEndSimulation}
                      >
                        End Simulation
                      </Button>
                      <Button
                        colorScheme="purple"
                        isDisabled={true}
                      >
                        Request Handoff (Coming Soon)
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>
              </>
            )}

            {/* Development Notes Card - Only show when simulation not active */}
            {!simulationActive && (
              <Card bg="gray.800" borderColor="gray.700">
                <CardHeader>
                  <HStack>
                    <InfoIcon color="blue.400" />
                    <Heading size="lg" color="blue.400">
                      Development Notes
                    </Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold" color="gray.300" mb={2}>
                        Architecture Plan
                      </Text>
                      <Text color="gray.400" mb={3}>
                        Based on the design in docs/videoScenarios
                      </Text>
                    </Box>

                    <Divider borderColor="gray.700" />

                    <Box>
                      <Text fontWeight="bold" color="gray.300" mb={2}>
                        Current Phase: 2a - Interactive Simulation (Text Input)
                      </Text>
                      <List spacing={3}>
                        <ListItem color="gray.400">
                          <HStack>
                            <CheckCircleIcon color="green.400" />
                            <Box>
                              <Text fontWeight="semibold">Phase 1: MVP</Text>
                              <Text fontSize="sm" color="gray.500">
                                ✓ Basic UI, scenario loading, initialization
                              </Text>
                            </Box>
                          </HStack>
                        </ListItem>
                        <ListItem color="gray.400">
                          <HStack align="start">
                            <CheckCircleIcon color="green.400" />
                            <Box>
                              <Text fontWeight="semibold">Phase 2a: Interactive Simulation (Current)</Text>
                              <Text fontSize="sm" color="gray.500">
                                ✓ Text input, real-time patient state, vitals monitoring, scoring
                              </Text>
                            </Box>
                          </HStack>
                        </ListItem>
                        <ListItem color="gray.400">
                          <HStack align="start">
                            <ListIcon as={InfoIcon} color="blue.400" mt={1} />
                            <Box>
                              <Text fontWeight="semibold">Phase 2b: Voice Integration</Text>
                              <Text fontSize="sm" color="gray.500">
                                Voice input/output, video playback
                              </Text>
                            </Box>
                          </HStack>
                        </ListItem>
                        <ListItem color="gray.400">
                          <HStack align="start">
                            <ListIcon as={InfoIcon} color="blue.400" mt={1} />
                            <Box>
                              <Text fontWeight="semibold">Phase 3: Advanced Features</Text>
                              <Text fontSize="sm" color="gray.500">
                                Automated handoff evaluation, instructor review, analytics
                              </Text>
                            </Box>
                          </HStack>
                        </ListItem>
                      </List>
                    </Box>

                    <Divider borderColor="gray.700" />

                    <Box>
                      <Text fontWeight="bold" color="gray.300" mb={2}>
                        Documentation
                      </Text>
                      <Text color="gray.400">
                        See docs/videoScenarios for full architecture and feature details
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        )}
      </Container>
    </Box>
  );
}